/**
 * Utility functions for managing chat drafts
 */

const DRAFT_PREFIX = 'pr-chat-draft';
const MAX_DRAFT_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_TOTAL_DRAFTS = 50; // Limit total number of drafts

interface DraftData {
  content: string;
  timestamp: number;
  conversationId?: string;
  clientId?: string;
}

/**
 * Clean up old drafts from localStorage
 */
export function cleanupOldDrafts(): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const draftKeys: string[] = [];
  
  // Collect all draft keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) {
      draftKeys.push(key);
    }
  }

  // Sort by timestamp and remove old/excess drafts
  const draftEntries = draftKeys
    .map(key => {
      try {
        const draft = localStorage.getItem(key);
        if (draft) {
          const draftData: DraftData = JSON.parse(draft);
          return { key, data: draftData };
        }
      } catch (error) {
        // Remove corrupted drafts
        localStorage.removeItem(key);
      }
      return null;
    })
    .filter(Boolean) as Array<{ key: string; data: DraftData }>;

  // Remove drafts older than MAX_DRAFT_AGE
  draftEntries.forEach(({ key, data }) => {
    if (now - data.timestamp > MAX_DRAFT_AGE) {
      localStorage.removeItem(key);
    }
  });

  // If still too many drafts, keep only the most recent ones
  const validDrafts = draftEntries.filter(({ data }) => 
    now - data.timestamp <= MAX_DRAFT_AGE
  );

  if (validDrafts.length > MAX_TOTAL_DRAFTS) {
    const sortedDrafts = validDrafts.sort((a, b) => b.data.timestamp - a.data.timestamp);
    const draftsToRemove = sortedDrafts.slice(MAX_TOTAL_DRAFTS);
    
    draftsToRemove.forEach(({ key }) => {
      localStorage.removeItem(key);
    });
  }
}

/**
 * Get total storage size used by drafts
 */
export function getDraftStorageSize(): number {
  if (typeof window === 'undefined') return 0;

  let totalSize = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += key.length + value.length;
      }
    }
  }
  return totalSize;
}

/**
 * Get count of saved drafts
 */
export function getDraftCount(): number {
  if (typeof window === 'undefined') return 0;

  let count = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) {
      count++;
    }
  }
  return count;
}

/**
 * Clear all drafts (useful for logout or reset)
 */
export function clearAllDrafts(): void {
  if (typeof window === 'undefined') return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Initialize draft cleanup (call once on app startup)
 */
export function initializeDraftCleanup(): void {
  // Clean up on startup
  cleanupOldDrafts();

  // Set up periodic cleanup (every hour)
  if (typeof window !== 'undefined') {
    setInterval(() => {
      cleanupOldDrafts();
    }, 60 * 60 * 1000); // 1 hour
  }
}